/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import React, { createContext, ReactNode, useContext } from 'react';

import { useAuth } from './AuthContext';
import { User, UserStatus } from '../models/User';
import api from '../services/api';

// Define the context type
interface UserContextType {
  searchUsers: (
    page?: number,
    limit?: number,
    query?: string,
    userStatus?: UserStatus,
  ) => Promise<{
    users: User[];
    page: number;
    totalPages: number;
    totalUsers: number;
  }>;
  getUser: (id: string) => Promise<User>;
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook for using the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { accessToken } = useAuth();
  
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('users/', reactAppApiUrl).toString();

  // Konfiguracja dla zapytań API (z tokenem)
  const getConfig = () => {
    return {
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
      withCredentials: true,
    };
  };

  // Search for users
  const searchUsers = async (
    page = 1,
    limit = 10,
    query = '',
    userStatus?: UserStatus,
  ): Promise<{
    users: User[];
    page: number;
    totalPages: number;
    totalUsers: number;
  }> => {
    if (page <= 0) {
      throw new Error('Nieprawidłowy numer strony');
    }
    if (limit <= 0) {
      throw new Error('Nieprawidłowy limit');
    }

    try {
      const response = await api.get(apiUrl, {
        ...getConfig(),
        params: {
          page,
          limit,
          query,
          userStatus,
        },
      });
      
      return {
        users: response.data.users,
        page: response.data.page,
        totalPages: response.data.totalPages,
        totalUsers: response.data.totalUsers,
      };
    } catch (error: any) {
      if (error.isAxiosError) {
        const response = error.response as AxiosResponse;
        throw new Error(response.data.message || 'Błąd serwera');
      }
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Get a single user by ID
  const getUser = async (id: string): Promise<User> => {
    try {
      const response = await api.get(`${apiUrl}${id}`, getConfig());
      return response.data;
    } catch (error: any) {
      if (error.isAxiosError) {
        const response = error.response as AxiosResponse;
        throw new Error(response.data.message || 'Błąd serwera');
      }
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  return (
    <UserContext.Provider
      value={{
        searchUsers,
        getUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
